/*
 * services-page.js — order drawer on the Services page (Services PR 4).
 * Talks ONLY to the signed Shopify App Proxy:
 *   POST {base}/services/shopify { service, whatsapp, locale, answers, notes, website }
 *        → 201 { ref, whatsapp: { number, text, url } }
 *        → 400 { error, field } · 401 { code: "login_required" } · 429 · 502
 * The server owns validation, the reference and the WhatsApp text; this file
 * only mirrors the required-field rules so the customer sees mistakes before
 * the round trip. Markup + every string: snippets/services-drawer.liquid.
 *
 * Hand-off: a blank tab is opened inside the submit gesture (pop-up blockers
 * allow that), then pointed at wa.me when the reference arrives. If saving
 * fails, the same tab gets a fallback message built from the answers, so
 * nothing typed is lost.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-services]');
  var drawer = document.querySelector('[data-drawer]');
  var overlay = document.querySelector('[data-drawer-overlay]');
  if (!root || !drawer) return;

  var base = (root.getAttribute('data-proxy-base') || '').replace(/\/+$/, '');
  // The App Proxy lives on this store's own domain. Anything that is not a plain
  // path (absolute or protocol-relative URL) is ignored: the drawer stays hidden
  // and the card buttons keep their no-JS wa.me links.
  if (base.charAt(0) !== '/' || base.charAt(1) === '/') return;
  var endpoint = base + '/services/shopify';
  var waDigits = (root.getAttribute('data-whatsapp') || '').replace(/\D/g, '') || '971544503515';
  var locale = (root.getAttribute('data-locale') || 'en').slice(0, 2) === 'ar' ? 'ar' : 'en';
  var form = drawer.querySelector('[data-services-form]');
  var received = drawer.querySelector('[data-received]');
  var titleNode = drawer.querySelector('[data-drawer-title]');
  var STORAGE_KEY = 'yalladj.services.whatsapp';
  var LIMIT_LOCK_MS = 30000;

  var current = null;   // active service key
  var isOpen = false;
  var opener = null;    // element to give focus back to
  var busy = false;
  var lockUntil = 0;

  function $(sel, ctx) { return (ctx || drawer).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || drawer).querySelectorAll(sel)); }
  function show(node, on) { if (node) node.hidden = !on; }

  /* ---------- service selection ---------- */

  function headFor(key) { return $('[data-drawer-head="' + key + '"]'); }
  function fieldsFor(key) { return form ? $('[data-fields="' + key + '"]', form) : null; }
  function serviceName(key) {
    var h = headFor(key);
    return h ? (h.getAttribute('data-service-name') || key) : key;
  }

  function returnPath(key) {
    var parts = (window.location.search || '').replace(/^\?/, '').split('&').filter(function (p) {
      return p && p.indexOf('service=') !== 0;
    });
    parts.push('service=' + encodeURIComponent(key));
    return window.location.pathname + '?' + parts.join('&');
  }

  function select(key) {
    if (!key || !headFor(key)) return false;
    current = key;
    $$('[data-drawer-head]').forEach(function (h) { show(h, h.getAttribute('data-drawer-head') === key); });
    if (titleNode) titleNode.textContent = serviceName(key);
    if (form) {
      $$('[data-fields]', form).forEach(function (fs) {
        var on = fs.getAttribute('data-fields') === key;
        fs.disabled = !on;
        fs.hidden = !on;
      });
    }
    $$('[data-login-link]').forEach(function (a) {
      var b = a.getAttribute('data-login-base');
      if (b) a.href = b + encodeURIComponent(returnPath(key));
    });
    // Monthly offers: offers.js adds the "offer order / normal order" choice
    // for a service that is part of the live offer.
    root.setAttribute('data-current-service', key);
    try { root.dispatchEvent(new CustomEvent('services:select', { detail: { key: key, form: form } })); } catch (err) { /* old browser */ }
    return true;
  }

  /* ---------- open / close ---------- */

  function setOpen(next, trigger) {
    isOpen = next;
    show(drawer, next);
    show(overlay, next);
    document.documentElement.classList.toggle('services-drawer-open', next);
    if (next) {
      opener = trigger || null;
      var close = $('[data-drawer-close]');
      window.requestAnimationFrame(function () { (close || drawer).focus(); });
    } else {
      if (received && !received.hidden) resetView();
      if (opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
    }
  }

  function trapTab(event) {
    var items = $$('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled])').filter(function (el) {
      return !el.hidden && el.offsetParent !== null && el.getAttribute('tabindex') !== '-1';
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest ? event.target : null;
    if (!target) return;
    var trigger = target.closest('[data-service]');
    if (trigger && root.contains(trigger)) {
      if (select(trigger.getAttribute('data-service'))) {
        event.preventDefault();
        setOpen(true, trigger);
      }
      return;
    }
    if (target.closest('[data-drawer-close]')) setOpen(false);
  });

  if (overlay) overlay.addEventListener('click', function () { setOpen(false); });

  document.addEventListener('keydown', function (event) {
    if (!isOpen) return;
    if (event.key === 'Escape') { setOpen(false); return; }
    if (event.key === 'Tab') trapTab(event);
  });

  /* ---------- WhatsApp number memory (best effort) ---------- */

  var waInput = form ? form.querySelector('[name="whatsapp"]') : null;
  function loadWhatsapp() {
    try {
      var v = window.localStorage.getItem(STORAGE_KEY);
      if (v && waInput) waInput.value = v;
    } catch (err) { /* storage blocked: keep the +971 default */ }
  }
  function saveWhatsapp(v) {
    try { window.localStorage.setItem(STORAGE_KEY, v); } catch (err) { /* ignore */ }
  }

  /* ---------- messages + errors ---------- */

  function msg(name) { return form ? (form.getAttribute('data-msg-' + name) || '') : ''; }
  function note(kind, on) { show($('[data-note="' + kind + '"]'), on); }

  function fieldWrap(name) {
    var fs = fieldsFor(current);
    return (fs && fs.querySelector('[data-field="' + name + '"]')) || (form && form.querySelector('[data-field="' + name + '"]'));
  }

  function setError(name, text) {
    var wrap = fieldWrap(name);
    if (!wrap) { note('generic', true); return; }
    var p = wrap.querySelector('[data-error-for]');
    if (p) { p.textContent = text; p.hidden = !text; }
    wrap.classList.toggle('is-invalid', !!text);
    var focusable = wrap.querySelector('input:not([type="hidden"]), textarea');
    if (!focusable) return;
    if (text) { focusable.setAttribute('aria-invalid', 'true'); focusable.focus(); }
    else focusable.removeAttribute('aria-invalid');
  }

  function clearErrors() {
    $$('[data-error-for]', form).forEach(function (p) { p.hidden = true; p.textContent = ''; });
    $$('.is-invalid', form).forEach(function (w) { w.classList.remove('is-invalid'); });
    $$('[aria-invalid]', form).forEach(function (el) { el.removeAttribute('aria-invalid'); });
    ['session', 'limit', 'generic'].forEach(function (k) { note(k, false); });
  }

  /* Server field → local message (keeps Arabic pages Arabic). Length errors
     (list, notes) keep the server text: maxlength makes them near-impossible. */
  function localMsg(field, serverText) {
    if (field === 'whatsapp') return msg('whatsapp');
    if (field === 'link') return msg('link');
    if (field === 'genres') return msg('pick-some');
    if (field === 'song' || field === 'djName') return msg('required');
    if (field === 'list' || field === 'notes') return serverText || msg('required');
    return msg('pick');
  }

  /* ---------- payload ---------- */

  function buildPayload() {
    var fd = new FormData(form); // disabled fieldsets are excluded by the browser
    var p = { service: current, whatsapp: '', locale: locale, answers: {}, notes: '', website: '' };
    fd.forEach(function (value, key) {
      var v = typeof value === 'string' ? value : '';
      if (key === 'whatsapp') p.whatsapp = v.trim();
      else if (key === 'notes') p.notes = v.trim();
      else if (key === 'website') p.website = v;
      else if (key === 'order_type') { if (v === 'offer') p.offer = true; }
      else if (key === 'genres') (p.answers.genres = p.answers.genres || []).push(v);
      else if (v.trim() !== '') p.answers[key] = v.trim();
    });
    return p;
  }

  function validate(payload) {
    var wa = payload.whatsapp.replace(/[\s\-().]/g, '');
    if (wa.indexOf('00') === 0) wa = '+' + wa.slice(2);
    if (!/^\+[1-9]\d{7,14}$/.test(wa)) return { field: 'whatsapp', text: msg('whatsapp') };
    var fs = fieldsFor(current);
    var wraps = fs ? $$('[data-field]', fs) : [];
    for (var i = 0; i < wraps.length; i++) {
      var w = wraps[i];
      var name = w.getAttribute('data-field');
      var required = w.hasAttribute('data-required');
      var chips = w.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      if (chips.length) {
        if (required && !w.querySelector('input:checked')) {
          return { field: name, text: chips[0].type === 'checkbox' ? msg('pick-some') : msg('pick') };
        }
        continue;
      }
      var input = w.querySelector('input, textarea');
      var v = input ? input.value.trim() : '';
      if (required && !v) return { field: name, text: msg('required') };
      if (input && input.type === 'url' && v && !/^https:\/\/\S+$/i.test(v)) return { field: name, text: msg('link') };
    }
    return null;
  }

  /* ---------- fallback text (server unreachable) ---------- */

  function summaryText() {
    var fs = fieldsFor(current);
    var parts = [];
    if (!fs) return '';
    $$('[data-field]', fs).forEach(function (w) {
      var labelNode = w.querySelector('[data-label]');
      var label = labelNode ? labelNode.textContent.trim() : '';
      var checked = $$('input:checked', w).map(function (i) {
        var l = i.closest('label');
        return l ? l.textContent.trim() : i.value;
      });
      var value = checked.length ? checked.join(', ') : '';
      if (!value) {
        var input = w.querySelector('input, textarea');
        value = input ? input.value.trim() : '';
      }
      if (label && value) parts.push(label + ': ' + value);
    });
    return parts.join(' · ');
  }

  function fallbackText(payload) {
    var intro = (form.getAttribute('data-wa-intro') || '').replace('[service]', serviceName(current));
    var lines = [intro, summaryText(), payload.notes, form.getAttribute('data-wa-outro') || ''];
    return lines.filter(Boolean).join('\n');
  }

  function waUrl(text) { return 'https://wa.me/' + waDigits + '?text=' + encodeURIComponent(text); }

  /* ---------- submit ---------- */

  function setBusy(on) {
    if (!on && Date.now() < lockUntil) return; // 429 lock still running
    busy = on;
    var btn = $('[data-submit]', form);
    if (!btn) return;
    btn.disabled = on;
    var label = $('[data-submit-label]', btn);
    if (label) label.textContent = btn.getAttribute(on ? 'data-busy-label' : 'data-label') || label.textContent;
  }

  function lockAfterLimit() {
    lockUntil = Date.now() + LIMIT_LOCK_MS;
    window.setTimeout(function () { lockUntil = 0; setBusy(false); }, LIMIT_LOCK_MS);
  }

  function showReceived(opts) {
    show(form, false);
    show(received, true);
    var refNode = $('[data-received-ref]');
    var unsaved = $('[data-received-unsaved]');
    var name = serviceName(current);
    if (opts.saved) {
      refNode.textContent = (refNode.getAttribute('data-pattern') || '').replace('[ref]', opts.ref).replace('[service]', name);
    } else {
      unsaved.textContent = (unsaved.getAttribute('data-pattern') || '').replace('[service]', name);
    }
    show(refNode, opts.saved);
    show(unsaved, !opts.saved);
    note('fallback', !opts.saved);
    var pre = $('[data-received-text]');
    if (pre) pre.textContent = opts.text;
    var link = $('[data-received-link]');
    if (link) { link.href = opts.url; window.requestAnimationFrame(function () { link.focus(); }); }
  }

  function resetView() {
    if (!form) return;
    form.reset();
    loadWhatsapp();
    clearErrors();
    show(form, true);
    show(received, false);
  }

  function pointTab(win, url) {
    if (!win) return;
    try { win.location.href = url; } catch (err) { /* tab already gone */ }
  }
  function closeTab(win) {
    if (!win) return;
    try { win.close(); } catch (err) { /* ignore */ }
  }

  function fallback(payload, win) {
    var text = fallbackText(payload);
    var url = waUrl(text);
    pointTab(win, url);
    showReceived({ saved: false, text: text, url: url });
  }

  if (form) {
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (busy || !current) return;
      clearErrors();
      var payload = buildPayload();
      var bad = validate(payload);
      if (bad) { setError(bad.field, bad.text); return; }
      saveWhatsapp(payload.whatsapp);

      // An offer order is paid on the site: this tab goes to the checkout, so
      // no WhatsApp tab is pre-opened for it.
      var win = null;
      if (!payload.offer) {
        try { win = window.open('', '_blank'); } catch (err) { win = null; }
      }
      setBusy(true);
      var status = 0;

      fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          status = res.status;
          return res.json().catch(function () { return null; });
        }, function () { return null; })
        .then(function (body) {
          if (status === 201 && body && body.ref && payload.offer && /^https:\/\//.test(body.checkoutUrl || '')) {
            window.location.href = body.checkoutUrl;
            return;
          }
          if (status === 409 && body && body.code === 'offer_ended') {
            closeTab(win);
            note('generic', true);
            try { root.dispatchEvent(new CustomEvent('services:offer-ended')); } catch (err) { /* ignore */ }
            return;
          }
          if (status === 201 && body && body.ref && body.whatsapp) {
            var text = body.whatsapp.text || '';
            // Only ever send the customer to WhatsApp; anything else is rebuilt locally.
            var url = /^https:\/\/wa\.me\//.test(body.whatsapp.url || '') ? body.whatsapp.url : waUrl(text);
            pointTab(win, url);
            showReceived({ saved: true, ref: body.ref, text: text, url: url });
            return;
          }
          if (status === 400 && body && body.field) { closeTab(win); setError(body.field, localMsg(body.field, body.error)); return; }
          if (status === 401 || status === 403) { closeTab(win); note('session', true); return; }
          if (status === 429) { closeTab(win); note('limit', true); lockAfterLimit(); return; }
          fallback(payload, win); // 0 (network), 5xx, 502, anything unexpected
        })
        .then(function () { setBusy(false); }, function () { setBusy(false); });
    });
  }

  /* ---------- boot ---------- */

  loadWhatsapp();
  var deep = /[?&]service=([a-z_]+)/.exec(window.location.search || '');
  if (deep && select(deep[1])) setOpen(true, null);
})();
